import { agentPromptDisplay } from "../model/agentPromptDisplay";
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import type { Dispatch, MutableRefObject, ReactNode, SetStateAction } from "react";
import type { HttpAgent, Message } from "@ag-ui/client";
import { useAuthState } from "@/hooks/useAuthState";
import { generateUuid } from "@/lib/uuid";
import { aguiClient } from "../services/aguiClient";
import { agentThreadService, isThreadRouteMissing } from "../services/agentThreadService";
import { DOCK_LABELS } from "../constants/pageAgentLabels";
import type { AgentTurn } from "../model/agentTurn";

export interface AgentThreadState {
  /** The conversation's id: minted here for a first conversation, adopted from the server after. */
  threadId: string;
  /** The one agent object for this thread. */
  agent: HttpAgent;
  /** False once the thread routes answered 404 — the Java side is rolled back and the dock hides. */
  available: boolean;
  loading: boolean;
  /** Why the stored conversation could not be loaded, in words a reader can act on. */
  error: string | null;
  /**
   * The transcript, restored turns and this session's alike. Here rather than in the dock because
   * the panel unmounts its conversation when it closes.
   */
  turns: AgentTurn[];
  setTurns: Dispatch<SetStateAction<AgentTurn[]>>;
  /**
   * Empties the transcript on this side after the server cleared it: the turns, and the messages the
   * agent object would otherwise send back. The thread id stays.
   */
  forgetTranscript: () => void;
  /**
   * Bumped by every `forgetTranscript`, so each surface driving the thread (the dock and the full
   * page) drops its own per-question state — trail, cards, chip — too.
   */
  transcriptGeneration: number;
  /**
   * The one run in flight on this thread, shared by every surface that drives it: they disable
   * together, and Stop from any of them addresses the same run.
   */
  run: AgentRunState;
}

export interface AgentRunState {
  busy: boolean;
  setBusy: (busy: boolean) => void;
  /** The id of the run in flight, or null between runs. */
  activeRunId: MutableRefObject<string | null>;
  /** True from Stop until the next question: ends the auto-continue loop and names the abort a Stop. */
  stopping: MutableRefObject<boolean>;
}

const AgentThreadContext = createContext<AgentThreadState | undefined>(undefined);

/** Turns a stored transcript into what the dock renders; tool and system messages are not turns. */
export function turnsFrom(messages: Message[]): AgentTurn[] {
  return messages.flatMap((message) => {
    if ((message.role !== "user" && message.role !== "assistant") || typeof message.content !== "string") return [];
    return [{ id: message.id, role: message.role, text: message.role === "user" ? agentPromptDisplay(message.content) : message.content, notice: null }];
  });
}

/**
 * The page-helper conversation: its identity, its agent object, and whatever transcript the server
 * already holds. Sits above the router so navigation — including the agent's own — keeps it.
 */
export function AgentThreadProvider({ children }: { children: ReactNode }) {
  const [threadId, setThreadId] = useState<string>(generateUuid);
  const [restoredMessages, setRestoredMessages] = useState<Message[]>([]);
  const [available, setAvailable] = useState(true);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [turns, setTurns] = useState<AgentTurn[]>([]);
  const [transcriptGeneration, setTranscriptGeneration] = useState(0);
  const [busy, setBusy] = useState(false);
  const activeRunId = useRef<string | null>(null);
  const stopping = useRef(false);
  const { isAuthenticated, user } = useAuthState();
  const userId: number | null = isAuthenticated && user ? user.id : null;

  // Keyed on threadId so the object is replaced exactly when the conversation changes.
  const agent = useMemo<HttpAgent>(() => aguiClient.pageAgent(threadId), [threadId]);

  // Applied to whichever agent is current: adopting the stored id builds a new agent, so setting
  // messages inside the fetch would put them on the object about to be discarded.
  useEffect(() => {
    if (restoredMessages.length === 0) return;
    agent.setMessages(restoredMessages);
  }, [agent, restoredMessages]);

  const forgetTranscript = useCallback(() => {
    agent.setMessages([]);
    setRestoredMessages([]);
    setTurns([]);
    setError(null);
    setTranscriptGeneration((current) => current + 1);
  }, [agent]);

  // Fetch the stored conversation once someone is signed in; keyed on who, never on the thread id.
  // A change of user starts from nothing, so the previous user's transcript never shows to the next.
  useEffect(() => {
    setThreadId(generateUuid());
    setRestoredMessages([]);
    setTurns([]);
    setError(null);
    if (userId === null) {
      setLoading(false);
      return;
    }
    let active = true;
    setLoading(true);
    agentThreadService.threads()
      .then(async (found) => {
        if (!active || found.length === 0) return;
        const newestId = found[0].threadId;
        if (newestId === undefined) throw new Error("The thread list returned a thread without an id");
        const stored = await agentThreadService.messages(newestId);
        if (!active) return;
        setThreadId(newestId);
        setRestoredMessages(stored);
        setTurns(turnsFrom(stored));
        setError(null);
      })
      .catch((err: unknown) => {
        if (!active) return;
        if (isThreadRouteMissing(err)) setAvailable(false);
        else setError(DOCK_LABELS.historyUnavailable);
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [userId]);

  const value = useMemo<AgentThreadState>(
    () => ({
      threadId, agent, available, loading, error, turns, setTurns, forgetTranscript, transcriptGeneration,
      run: { busy, setBusy, activeRunId, stopping },
    }),
    [threadId, agent, available, loading, error, turns, forgetTranscript, transcriptGeneration, busy]);

  return <AgentThreadContext.Provider value={value}>{children}</AgentThreadContext.Provider>;
}

/** @throws outside the provider: a dock with no conversation has nothing to render. */
export function useAgentThread(): AgentThreadState {
  const context = useContext(AgentThreadContext);
  if (context === undefined) throw new Error("useAgentThread must be used inside an AgentThreadProvider");
  return context;
}
